import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { getAllEmployees } from "../../core/api/useGetEmployeeAll";
import {
  getAllTerminations,
  createTermination,
  updateTermination,
  deleteTermination,
} from "../../core/api/useTermination";
import { terminationType } from "../../core/hardcoded/teminationType";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import Datatable from "../../core/common/dataTable/dataTable";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";
import Table from "../../core/common/dataTable/index";
import { Modal } from "antd";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { usePermission } from "../../core/api/useRolePermission";
import { fetchEmployeeImage } from "../../core/api/fetchEmployeeImage";

interface TerminationRecord {
  termination_id: number;
  employee_id: string;
  termination_type: string;
  notice_date: string;
  reason: string;
  termination_date: string;
}

interface Employee {
  employee_id: string;
  firstname: string;
  lastname: string;
}

const Termination = () => {
  const [noticeError, setNoticeError] = useState("");
  const [terminationData, setTerminationData] = useState<TerminationRecord[]>(
    []
  );
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedTerminationId, setSelectedTerminationId] = useState<
    number | null
  >(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [resetSearchTrigger, setResetSearchTrigger] = useState<number>(0);
  const empToken = useAuthToken();
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  // Permissions
  const canCreate = usePermission("Termination", "create");
  const canUpdate = usePermission("Termination", "write");
  const canDelete = usePermission("Termination", "delete");
  const canRead = usePermission("Termination", "read");

  const [newFormData, setNewFormData] = useState({
    employee_id: "",
    termination_type: "",
    notice_date: "",
    reason: "",
    termination_date: "",
  });

  const [editFormData, setEditFormData] = useState({
    employee_id: "",
    termination_type: "",
    notice_date: "",
    reason: "",
    termination_date: "",
  });

  useEffect(() => {
    fetchTerminations();
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      const urlMap: Record<string, string> = {};

      for (const emp of employees ?? []) {
        urlMap[emp.employee_id] = await fetchEmployeeImage(
          emp.employee_id,
          empToken
        );
      }

      for (const record of terminationData ?? []) {
        if (!urlMap[record.employee_id]) {
          urlMap[record.employee_id] = await fetchEmployeeImage(
            record.employee_id,
            empToken
          );
        }
      }

      setImageUrls(urlMap);
    };

    if ((employees?.length ?? 0) > 0 || (terminationData?.length ?? 0) > 0) {
      fetchImages();
    }
  }, [employees, terminationData, empToken]);

  const fetchTerminations = async () => {
    try {
      const res = await getAllTerminations(empToken);

      // res itself is already an array
      const data = Array.isArray(res) ? res : [];

      // Add `id` alias if your Table needs rowKey="id"
      const keyedData = data.map((item: TerminationRecord) => ({
        ...item,
        id: item.termination_id,
      }));

      setTerminationData(keyedData as TerminationRecord[]);
    } catch (err) {
      console.error("Failed to fetch terminations:", err);
      setTerminationData([]); // fallback
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await getAllEmployees(empToken);
      const data = Array.isArray(res) ? res : [];
      setEmployees(data);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      setEmployees([]); // fallback
    }
  };

  const handleDeleteClick = (terminationId: number) => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete terminations.");
      return;
    }
    setSelectedTerminationId(terminationId);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete terminations.");
      return;
    }

    if (selectedTerminationId === null) return;

    try {
      await deleteTermination(empToken, selectedTerminationId);
      await fetchTerminations();
      setSelectedTerminationId(null);
      setDeleteModalVisible(false);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail || "Error deleting termination.";
      console.error(`Delete failed: ${backendMessage}`);
    }
  };

  const validateNoticeAndTermination = (
    notice: string,
    termination: string
  ) => {
    if (notice && termination) {
      const noticeDate = new Date(notice);
      const terminationDate = new Date(termination);
      if (noticeDate >= terminationDate) {
        setNoticeError("Notice Date must be before Termination Date.");
      } else {
        setNoticeError("");
      }
    } else {
      setNoticeError("");
    }
  };

  const terminationColumns = [
    {
      title: "Resigning Employee",
      dataIndex: "employee_id",
      key: "employee_id",
      render: (text: string, record: TerminationRecord) => {
        const emp = employees.find((e) => e.employee_id === record.employee_id);
        return (
          <div className="d-flex align-items-center">
            <img
              src={
                imageUrls[record.employee_id] || "/assets/img/defaultavatar.png"
              }
              className="img-fluid rounded-circle me-2"
              style={{ width: 40, height: 40 }}
              alt={
                emp ? `${emp.firstname} ${emp.lastname}` : record.employee_id
              }
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "/assets/img/defaultavatar.png";
              }}
            />
            <span>
              {emp ? `${emp.firstname} ${emp.lastname}` : record.employee_id}
            </span>
          </div>
        );
      },
    },
    {
      title: "Termination Type",
      dataIndex: "termination_type",
      key: "termination_type",
    },
    {
      title: "Notice Date",
      dataIndex: "notice_date",
      key: "notice_date",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Termination Date",
      dataIndex: "termination_date",
      key: "termination_date",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TerminationRecord) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            onClick={(e) => {
              if (!canUpdate) {
                toast.warning(
                  "You don't have permission to edit terminations."
                );
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setEditFormData({
                employee_id: record.employee_id,
                termination_type: record.termination_type,
                notice_date: record.notice_date,
                reason: record.reason,
                termination_date: record.termination_date,
              });
              setSelectedTerminationId(record.termination_id);
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_termination" : undefined}
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              handleDeleteClick(record.termination_id);
            }}
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  const handleCreate = async () => {
    if (!canCreate) {
      toast.warning("You don't have permission to create terminations.");
      return;
    }

    if (
      !newFormData.employee_id ||
      !newFormData.termination_type ||
      !newFormData.notice_date ||
      !newFormData.reason ||
      !newFormData.termination_date
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }

    if (noticeError) {
      toast.warning("Please fix the date validation errors.");
      return;
    }

    try {
      await createTermination(empToken, newFormData);
      setNewFormData({
        employee_id: "",
        termination_type: "",
        notice_date: "",
        reason: "",
        termination_date: "",
      });
      await fetchTerminations();
      setResetSearchTrigger(Date.now());
      toast.success("Termination added successfully.");
    } catch (err) {
      console.error("Error creating termination:", err);
    }
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      toast.warning("You don't have permission to update terminations.");
      return;
    }

    if (selectedTerminationId && !noticeError) {
      try {
        await updateTermination(empToken, selectedTerminationId, {
          ...editFormData,
        });
        await fetchTerminations();
        setEditFormData({
          employee_id: "",
          termination_type: "",
          notice_date: "",
          reason: "",
          termination_date: "",
        });
        setSelectedTerminationId(null);
        setResetSearchTrigger(Date.now());
        toast.success("Termination updated successfully.");
      } catch (err: any) {
        console.error("Error updating termination:", err);
      }
    } else {
      toast.warning("Please fix the date validation errors.");
    }
  };

  const resetAddForm = () => {
    setNewFormData({
      employee_id: "",
      termination_type: "",
      notice_date: "",
      reason: "",
      termination_date: "",
    });
    setNoticeError("");
  };

  const resetEditForm = () => {
    setEditFormData({
      employee_id: "",
      termination_type: "",
      notice_date: "",
      reason: "",
      termination_date: "",
    });
    setNoticeError("");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Termination</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Termination</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={(e) => {
                  if (!canCreate) {
                    toast.warning(
                      "You don't have permission to create terminations."
                    );
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  resetAddForm();
                }}
                data-bs-toggle={canCreate ? "modal" : undefined}
                data-bs-target={canCreate ? "#new_termination" : undefined}
              >
                <i className="ti ti-circle-plus me-2" /> Add Termination
              </button>
            </div>
            <div className="ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <Table
          columns={terminationColumns}
          dataSource={terminationData}
          Selection={false}
          rowKey="termination_id"
        />
      </div>

      <div className="modal fade" id="new_termination">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Termination</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                onClick={resetAddForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Employee <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={newFormData.employee_id}
                      onChange={(e) =>
                        setNewFormData({
                          ...newFormData,
                          employee_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {emp.firstname} {emp.lastname} ({emp.employee_id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Termination Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={newFormData.termination_type}
                      onChange={(e) =>
                        setNewFormData({
                          ...newFormData,
                          termination_type: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Termination Type</option>
                      {terminationType.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Notice Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={newFormData.notice_date}
                      onChange={(e) => {
                        const updatedDate = e.target.value;
                        const updatedForm = {
                          ...newFormData,
                          notice_date: updatedDate,
                        };
                        setNewFormData(updatedForm);
                        validateNoticeAndTermination(
                          updatedDate,
                          newFormData.termination_date
                        );
                      }}
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Termination Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={newFormData.termination_date}
                      onChange={(e) => {
                        const updatedDate = e.target.value;
                        const updatedForm = {
                          ...newFormData,
                          termination_date: updatedDate,
                        };
                        setNewFormData(updatedForm);
                        validateNoticeAndTermination(
                          newFormData.notice_date,
                          updatedDate
                        );
                      }}
                    />
                    {noticeError && (
                      <div className="col-md-12 mb-2">
                        <span className="text-danger">{noticeError}</span>
                      </div>
                    )}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      value={newFormData.reason}
                      onChange={(e) =>
                        setNewFormData({
                          ...newFormData,
                          reason: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  onClick={resetAddForm}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={(e) => {
                    (e.currentTarget as HTMLButtonElement).blur();
                    handleCreate();
                  }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="modal fade" id="edit_termination">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Termination</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                onClick={resetEditForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Employee <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={editFormData.employee_id}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          employee_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {emp.firstname} {emp.lastname} ({emp.employee_id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Termination Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={editFormData.termination_type}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          termination_type: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Termination Type</option>
                      {terminationType.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Notice Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={editFormData.notice_date}
                      onChange={(e) => {
                        const updatedDate = e.target.value;
                        const updatedForm = {
                          ...editFormData,
                          notice_date: updatedDate,
                        };
                        setEditFormData(updatedForm);
                        validateNoticeAndTermination(
                          updatedDate,
                          editFormData.termination_date
                        );
                      }}
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Termination Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={editFormData.termination_date}
                      onChange={(e) => {
                        const updatedDate = e.target.value;
                        const updatedForm = {
                          ...editFormData,
                          termination_date: updatedDate,
                        };
                        setEditFormData(updatedForm);
                        validateNoticeAndTermination(
                          editFormData.notice_date,
                          updatedDate
                        );
                      }}
                    />
                    {noticeError && (
                      <div className="col-md-12 mb-2">
                        <span className="text-danger">{noticeError}</span>
                      </div>
                    )}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      value={editFormData.reason}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          reason: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white border me-2"
                  onClick={resetEditForm}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdate}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this termination record?</p>
      </Modal>
    </div>
  );
};

export default Termination;
